import { yupResolver } from '@hookform/resolvers/yup';
import { BaseModal } from '@src/common/components/BaseModal';
import { Divider } from '@src/common/components/Divider';
import { IconButton } from '@src/common/components/IconButton';
import { CloseConfirmModalFooter } from '@src/common/components/modal/CloseConfirmModalFooter';
import { Stack } from '@src/common/components/Stack';
import { TextInput } from '@src/common/components/TextInput';
import { useToast } from '@src/common/hooks/useToast';
import { theme } from '@src/common/styles/Theme';
import { DocumentsClient } from '@src/documents/services/DocumentsClient';
import { Edit01 } from '@untitled-ui/icons-react';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { InferType } from 'yup';
import { object as yupObject, string } from 'yup';

interface RenamingModalProps {
  documentId?: string;
  currentName?: string;
  onSubmit?: (newName?: string) => void;
}

const RenamingSchema = yupObject()
  .shape({
    name: string(),
  })
  .required();

export const RenamingOption = ({
  documentId,
  currentName,
  onSubmit,
}: RenamingModalProps) => {
  const { t } = useTranslation();
  const { showSuccessToast, showErrorToast } = useToast();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { control, handleSubmit, reset } = useForm({
    resolver: yupResolver(RenamingSchema),
    mode: 'onSubmit',
    defaultValues: {
      name: currentName || '',
    },
  });

  useEffect(() => {
    if (isModalOpen) {
      reset({ name: currentName || '' });
    }
  }, [isModalOpen, currentName, reset]);

  const submitForm = async (data: InferType<typeof RenamingSchema>) => {
    if (!documentId) {
      showErrorToast(t('documents.rename.error'));
    } else {
      try {
        await DocumentsClient.rename(documentId, data.name);
        showSuccessToast(t('documents.rename.success'));
        onSubmit?.(data.name);
        setIsModalOpen(false);
      } catch {
        showErrorToast(t('documents.rename.error'));
      }
    }
  };

  return (
    <Stack>
      {documentId && (
        <IconButton
          onClick={() => setIsModalOpen(true)}
          aria-label={t('documents.name')}
        >
          <Edit01 />
        </IconButton>
      )}

      <BaseModal
        title={t('documents.name')}
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      >
        <form
          onSubmit={(e) => {
            e.stopPropagation();
            handleSubmit(submitForm)(e);
          }}
        >
          <Stack direction="column" gap={theme.spacing['4']}>
            <Controller
              name="name"
              control={control}
              render={({ field }) => <TextInput {...field} autoFocus />}
            />
          </Stack>
          <Divider
            style={{
              marginTop: theme.spacing['8'],
              marginBottom: theme.spacing['6'],
            }}
          />
          <CloseConfirmModalFooter
            onClose={() => setIsModalOpen(false)}
            confirm={() => {}}
            isSubmitType={true}
            closeLabel={t('cancel')}
            confirmLabel={t('save')}
          />
        </form>
      </BaseModal>
    </Stack>
  );
};
